import { noticeError } from '../../ui/newrelic/newrelic';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { UserInfo, AuthService, AuthStorageService } from '../../domain/auth';
import { UserInfoDataProvider } from '../../domain/static-content/static-content.static-generation.model';
import { getQueryValue } from '../router/router';
import { CryptoUtils } from '../../domain/auth/crypto.utils';
import { Logger } from '../logger/logger';
import { authService, type AuthState } from '../../domain/auth/auth.service';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { getLocale } from '../../util/locales/locales';

declare const window: Window & { dataLayer: Record<string, unknown>[] };

interface AuthProps {
  requiresAuthentication: boolean;
  userInfoDataProvider: UserInfoDataProvider;
  displayLoggedInUser: boolean;
  onPostLogin?: () => void;
}

export const useAuth = ({ requiresAuthentication = true, userInfoDataProvider, onPostLogin }: AuthProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [authError, setAuthError] = useState<string | undefined>();
  const [redirectPath, setRedirectPath] = useState<string | undefined>();
  const [authState, _setAuthState] = useState<AuthState>(authService.getState());

  const { data: userInfo, error: userInfoError } = useQuery<any, any, UserInfo>(
    ['userInfo', getLocale().RFC5646],
    async () =>
      AuthService.getUserInfoFromCustomerProfile().then((userInfo) => {
        AuthStorageService.saveUserInfo(userInfo);
        return userInfo;
      }),
    {
      enabled: userInfoDataProvider !== 'none' && authState === 'auth-completed',
      initialData: AuthStorageService.getUserInfo(),
    }
  );

  // Handles the state parameter
  // 1. sets the auth state to logged-in
  // 2. sets the redirect path
  // 3. parses the additional data and pushes it to the data layer
  const handleAuthStateParameter = (state: string) => {
    Logger.debug('(handleAuthStateParameter fn)', 'Setting auth state to logged-in');
    authService.setState('logged-in');

    const [originPathname, _csrf, additionalData] = state.split('|');
    setRedirectPath(originPathname);
    const [pathname, search] = originPathname.split('?');

    try {
      if (additionalData) {
        const parsedAdditionalData: Record<string, any> = JSON.parse(CryptoUtils.decodeFromBase64Url(additionalData));
        const referrer = parsedAdditionalData.analytics?.ga_referrer;
        window.dataLayer?.push({ original_referrer: referrer ?? undefined });
      }
    } catch (error) {
      noticeError(error, { context: 'useAuth' });
    }

    router.push(
      originPathname,
      {
        pathname,
        search,
      },
      { shallow: true }
    );
  };

  const handleAuthenticationFlow = async () => {
    Logger.debug('(handleAuthenticationFlow fn)', `authState: ${authState}`);

    const loginErrorQueryValue = [getQueryValue(router, 'error'), getQueryValue(router, 'error_description')].filter(Boolean);
    if (loginErrorQueryValue.length > 0) {
      handleAuthError(new Error(loginErrorQueryValue[0]));
      return;
    }

    const logout = getQueryValue(router, 'logout');
    if (logout) {
      AuthService.logout();
      return;
    }

    try {
      Logger.debug('(handleAuthenticationFlow fn)', 'Setting auth state to auth-in-progress');
      authService.setState('auth-in-progress');

      const hasValidToken = await checkAuthStatus();

      // Case 1: Has valid token
      if (hasValidToken) {
        Logger.debug('(handleAuthenticationFlow fn)', 'Has valid token, setting auth state to auth-completed');
        authService.setState('auth-completed');
        return;
      }

      // Case 2: Has auth code flow
      const queryCode = getQueryValue(router, 'code');
      const queryState = getQueryValue(router, 'state');

      if (!queryCode || !queryState) {
        Logger.debug('(handleAuthenticationFlow fn)', 'No code or state query parameter found, redirecting to login');
        return AuthService.logIn(redirectPath);
      }

      await handleTokenValidation(queryCode, queryState);
      if (authState !== 'logged-in') {
        Logger.debug('(handleAuthenticationFlow fn)', 'Setting auth state parameter');
        handleAuthStateParameter(queryState);
      }
    } catch (error) {
      handleAuthError(error);
    }
  };

  const checkAuthStatus = async () => {
    if (process.env.NEXT_PUBLIC_WARP_AUTH_ENABLED !== 'true') {
      Logger.debug('(checkAuthStatus fn)', 'WARP enabled: false, checking access token');
      if (AuthStorageService.getAccessToken()) {
        Logger.debug('(checkAuthStatus fn)', 'Access token found');
        return true;
      } else {
        Logger.debug('(checkAuthStatus fn)', 'No access token found');
        return false;
      }
    }

    try {
      if (!AuthStorageService.getIdToken()) {
        Logger.debug('(checkAuthStatus fn)', 'No ID token found');
        return false;
      }
      const hasValidToken = await AuthService.hasValidToken();
      Logger.debug('(checkAuthStatus fn)', 'hasValidToken', hasValidToken);
      if (hasValidToken) {
        Logger.debug('(checkAuthStatus fn)', 'Valid token found');
        return true;
      }
    } catch (error) {
      Logger.debug('(checkAuthStatus fn)', `An error occurred while checking the token: ${error.message}`);
      return false;
    }
  };

  const handleTokenValidation = async (code: string, state: string) => {
    Logger.debug('(handleTokenValidation fn)', 'validating token');
    const validationResult = await AuthService.canGetToken(code, state);

    if (!validationResult.success) {
      return handleAuthError(validationResult.error);
    }

    Logger.debug('(handleTokenValidation fn)', 'Token validation successful, setting auth state to loading-token');
    authService.setState('loading-token');

    try {
      await AuthService.getToken(code);
      Logger.debug('(handleTokenValidation fn)', 'Token validation successful, setting auth state to auth-completed');
      authService.setState('auth-completed');
    } catch (err) {
      noticeError(err, { context: 'getToken' });
      Logger.debug('(handleTokenValidation fn)', 'Token validation failed, setting auth state to auth-error');
      authService.setState('auth-error');
      setAuthError(err);
    }
  };

  const onLogout = () => {
    // Just responsible for setting the state, the actual logout is handled in the useEffect
    Logger.debug('(onLogout fn)', 'Setting auth state to logging-out');
    authService.setState('logging-out');
  };

  const handleAuthError = (error: Error | undefined) => {
    if (!error) {
      return;
    }

    Logger.debug('(handleAuthError fn)', `error: ${error.message}`);
    noticeError(error, { context: 'getToken' });
    authService.setState('auth-error');
    setAuthError(error.message);

    let updatedRedirectPath = redirectPath;
    if (error.message === 'invalid_request' || error.message === 'access_denied') {
      const queryState = getQueryValue(router, 'state');
      updatedRedirectPath = queryState?.split('|')[0];
    }
    AuthService.logIn(updatedRedirectPath);
    setRedirectPath(undefined);
  };

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = authService.subscribe(_setAuthState);
    Logger.debug('(useEffect fn)', 'Subscribed to auth state changes');
    return () => unsubscribe();
  }, []);

  // Update authentication flow check
  useEffect(() => {
    Logger.debug(
      '(useEffect fn)',
      `router.isReady: ${router.isReady}, requiresAuthentication: ${requiresAuthentication}, authError: ${authError}, authState: ${authState}`
    );
    if (
      !router.isReady ||
      !requiresAuthentication ||
      authError ||
      authState === 'auth-error' ||
      authState === 'auth-completed' ||
      authState === 'auth-in-progress' ||
      authState === 'loading-token' ||
      authState === 'logging-out'
    ) {
      Logger.debug('(useEffect fn)', 'Not handling authentication flow, early return');
      return;
    }
    handleAuthenticationFlow();
  }, [router.query, authState]);

  useEffect(() => {
    if (authState === 'auth-completed') {
      Logger.debug(`authState: ${authState}`, 'Enabling all queries');
      queryClient.setDefaultOptions({
        queries: {
          enabled: true,
        },
      });

      if (userInfo) {
        Logger.debug(`authState: ${authState}`, 'Calling onPostLogin callback');
        onPostLogin?.();
      }
    } else if (authState === 'logging-out') {
      Logger.debug(`authState: ${authState}`, 'Disabling all queries');
      queryClient.setDefaultOptions({
        queries: {
          enabled: false,
        },
      });

      Logger.debug(`authState: ${authState}`, 'Logging out');
      AuthService.logout();
    }
  }, [authState, userInfo]);

  // just used for logging
  useIsomorphicLayoutEffect(() => {
    Logger.debug('Initializing useAuth hook, WARP enabled:', process.env.NEXT_PUBLIC_WARP_AUTH_ENABLED);
  }, []);

  return { isAuthenticated: authState === 'auth-completed', error: authError || userInfoError, userInfo, onLogout };
};
